import { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  Users, Eye, BarChart3, LogOut, ChevronRight, Search, 
  ArrowLeft, User, FileText, Edit, Trash2, Save, X, Plus,
  ChevronUp, ChevronDown, Target, CheckCircle2, Circle, Heart, Sparkles,
  Download, Upload, BookOpen
} from "lucide-react";
import { useAuth, apiCall } from "../App";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { CoachReportsPanel, ClientProfilingDownload } from "../components/ReportsPanel";
import DiagnosticResultsView from "../components/DiagnosticResultsView";
import KoshaQuizResultsView from "../components/KoshaQuizResultsView";
import { CoachJourneyView, InterventionPlanManager, EscalationPanel } from "../components/JourneyPanel";
import EngagementScores from "../components/EngagementScores";
import AdminViewBreadcrumb from "../components/AdminViewBreadcrumb";
import ReportPreview from "../components/ReportPreview";
import DemoVideoButton from "../components/DemoVideo";
import GuideViewer from "../components/GuideViewer";
const CoachClientDetail = lazy(() => import("../components/CoachClientDetail"));

const CoachDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewAsCoachId = searchParams.get("view_as_coach_id");
  const fromTab = searchParams.get("from_tab");
  const isAdminViewing = user?.role === "admin" && viewAsCoachId;
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewingCoach, setViewingCoach] = useState(null);
  const [notes, setNotes] = useState([]);
  const [showGuide, setShowGuide] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [clientAssignments, setClientAssignments] = useState([]);
  const [selectedForAssignment, setSelectedForAssignment] = useState([]);
  const [clientModule, setClientModule] = useState("relationship");
  const [reportAssessmentTypes, setReportAssessmentTypes] = useState(null);
  const [clientReports, setClientReports] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");

  const togglePracticeSelect = (pid) => {
    setSelectedForAssignment(prev => prev.includes(pid) ? prev.filter(id => id !== pid) : [...prev, pid]);
  };

  const filteredClients = clients.filter(c => 
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.client_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );


  useEffect(() => {
    const fetchClients = async () => {
      if (viewAsCoachId && user?.role === "admin") {
        // Admin viewing a specific coach's dashboard
        const result = await apiCall("GET", `/admin/coach/${viewAsCoachId}/clients`, null, token);
        if (result.success) {
          setClients(result.data.clients || []);
          setViewingCoach(result.data.coach);
        }
      } else {
        // Coach viewing their own dashboard
        const result = await apiCall("GET", "/coach/clients", null, token);
        if (result.success) {
          setClients(result.data.clients || []);
        }
      }
      setLoading(false);
    };
    fetchClients();
  }, [token, viewAsCoachId, user?.role]);

  const handleSelectClient = async (client) => {
    setSelectedClient(client);
    setSelectedForAssignment([]);
    const [detailRes, notesRes, recRes, assignRes] = await Promise.all([
      apiCall("GET", `/coach/client/${client.user_id}`, null, token),
      apiCall("GET", `/notes/client/${client.user_id}`, null, token),
      apiCall("GET", `/practices/recommend/${client.user_id}`, null, token),
      apiCall("GET", `/practice-assignments/client/${client.user_id}`, null, token),
    ]);
    if (detailRes.success) setClientDetail(detailRes.data);
    if (notesRes.success) setNotes(notesRes.data.notes || []);
    if (recRes.success) setRecommendations(recRes.data);
    if (assignRes.success) setClientAssignments(assignRes.data.assignments || []);
    // Fetch report data for the selected client
    const currentMod = clientModule;
    const [typesRes, reportsRes] = await Promise.all([
      apiCall("GET", `/reports-3a/assessment-types?module=${currentMod}`, null, token),
      apiCall("GET", `/reports-3a/list?client_id=${client.user_id}`, null, token),
    ]);
    if (typesRes.success) setReportAssessmentTypes(typesRes.data);
    if (reportsRes.success) setClientReports(reportsRes.data.reports || []);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cosmic-void flex items-center justify-center">
        <div className="text-divine-gold animate-pulse font-heading text-2xl">
          Loading Coach Dashboard...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cosmic-void" data-testid="coach-dashboard">
      {/* Admin View Breadcrumb */}
      {isAdminViewing && (
        <AdminViewBreadcrumb
          viewerRole="admin"
          fromTab={fromTab}
          entityType="coach"
          entityName={viewingCoach?.name || "Coach"}
          testId="coach-view-breadcrumb"
        />
      )}
      {/* Navigation */}
      <nav className={`fixed ${isAdminViewing ? 'top-10' : 'top-0'} w-full z-50 backdrop-blur-md border-b border-divine-gold/10 bg-cosmic-void/80`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {!isAdminViewing && (
              <button 
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-cosmic-light/20 rounded-lg transition-colors text-chakra-crown/70 hover:text-divine-gold"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div className="flex items-center gap-2">
              <span className="text-3xl text-divine-gold">ॐ</span>
              <span className="font-heading text-xl text-divine-gold tracking-wider hidden md:inline">
                SIWON HARMONY
              </span>
              <Badge className="bg-cosmic-light text-divine-gold ml-2">COACH: {viewingCoach?.name || user?.name}</Badge>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <DemoVideoButton className="text-sm" />
            <button
              onClick={() => setShowGuide(true)}
              className="p-2 hover:text-divine-gold transition-colors text-chakra-crown/70"
              title="Coach Guide"
              data-testid="open-coach-guide-btn"
            >
              <BookOpen className="w-5 h-5" />
            </button>
            {!isAdminViewing && (
              <>
                <span className="text-chakra-crown/70 hidden md:inline">{user?.name}</span>
                <button 
                  onClick={logout}
                  className="p-2 hover:text-divine-gold transition-colors text-chakra-crown/70"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className={`${isAdminViewing ? 'pt-32' : 'pt-24'} pb-12 px-6`}>
        <div className="max-w-7xl mx-auto">
          {showGuide ? (
            <GuideViewer slug="coach" token={token} canDownload={false} onBack={() => setShowGuide(false)} />
          ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Clients List */}
            <div className="lg:col-span-1 space-y-4">
              {/* Escalation Alerts */}
              {!viewAsCoachId && <EscalationPanel token={token} />}
              <div className="sacred-card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-xl text-divine-gold">
                    <Users className="w-5 h-5 inline mr-2" />
                    Clients
                  </h2>
                  <span className="text-chakra-crown/60">{clients.length} total</span>
                </div>

                {/* Search */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-divine-gold/40" />
                  <Input
                    placeholder="Search clients..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown"
                    data-testid="client-search"
                  />
                </div>

                {/* Client List */}
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {filteredClients.map((client) => (
                    <button
                      key={client.user_id}
                      onClick={() => handleSelectClient(client)}
                      className={`w-full p-4 rounded-xl text-left transition-all ${
                        selectedClient?.user_id === client.user_id
                          ? "bg-divine-gold/20 border border-divine-gold"
                          : "bg-cosmic-light/10 border border-transparent hover:border-divine-gold/30"
                      }`}
                      data-testid={`client-${client.user_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-divine-gold font-medium">{client.name}</p>
                          <p className="text-chakra-crown/60 text-sm">{client.client_id}</p>
                        </div>
                        <ChevronRight className={`w-4 h-4 ${
                          selectedClient?.user_id === client.user_id ? "text-divine-gold" : "text-chakra-crown/40"
                        }`} />
                      </div>
                      {client.profiling_complete && (
                        <div className="mt-2 flex gap-2">
                          <Badge variant="outline" className="text-xs border-divine-gold/30 text-divine-gold/70">
                            Trinity: {client.scores?.trinity_gap?.toFixed(0) || 0}%
                          </Badge>
                          <Badge variant="outline" className="text-xs border-chakra-crown/30 text-chakra-crown/70">
                            {client.scores?.guna?.dominant || "N/A"}
                          </Badge>
                        </div>
                      )}
                    </button>
                  ))}
                  
                  {filteredClients.length === 0 && (
                    <p className="text-center text-chakra-crown/50 py-8">No clients found</p>
                  )}
                </div>
              </div>

              {/* Reports Section */}
              {!viewAsCoachId && (
                <div className="sacred-card">
                  <button onClick={() => setActiveTab(activeTab === "reports" ? "overview" : "reports")} className="w-full flex items-center justify-between" data-testid="coach-reports-toggle">
                    <h2 className="font-heading text-lg text-divine-gold flex items-center gap-2">
                      <BarChart3 className="w-5 h-5" /> Reports
                    </h2>
                    {activeTab === "reports" ? <ChevronUp className="w-4 h-4 text-divine-gold" /> : <ChevronDown className="w-4 h-4 text-divine-gold" />}
                  </button>
                  {activeTab === "reports" && (
                    <div className="mt-4 pt-4 border-t border-divine-gold/10">
                      <CoachReportsPanel token={token} />
                    </div>
                  )}
                </div>
              )}

              {/* Client Engagement Health */}
              {!viewAsCoachId && (
                <div className="sacred-card" data-testid="coach-engagement-section">
                  <h2 className="font-heading text-lg text-divine-gold flex items-center gap-2 mb-3">
                    <Target className="w-5 h-5" /> Client Engagement
                  </h2>
                  <EngagementScores token={token} role="coach" />
                </div>
              )}
            </div>

            {/* Client Detail */}
            <div className="lg:col-span-2 space-y-6">
              {selectedClient && clientDetail ? (
                <Suspense fallback={<div className="p-8 text-center text-divine-gold/50 animate-pulse">Loading client details...</div>}>
                <CoachClientDetail
                  token={token} user={user}
                  selectedClient={selectedClient} clientDetail={clientDetail}
                  clientModule={clientModule} setClientModule={(mod) => { setClientModule(mod); }}
                  notes={notes} setNotes={setNotes}
                  recommendations={recommendations}
                  clientAssignments={clientAssignments} setClientAssignments={setClientAssignments}
                  selectedForAssignment={selectedForAssignment} setSelectedForAssignment={setSelectedForAssignment}
                  togglePracticeSelect={togglePracticeSelect}
                  reportAssessmentTypes={reportAssessmentTypes} setReportAssessmentTypes={setReportAssessmentTypes}
                  clientReports={clientReports} setClientReports={setClientReports}
                  activeTab={activeTab} setActiveTab={setActiveTab}
                />
                </Suspense>
              ) : (
                <div className="sacred-card flex items-center justify-center h-96">
                  <p className="text-chakra-crown/50">Select a client to view details</p>
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CoachDashboard;
